import { NetLogicAPI } from "@/api/NetLogicAPI";
import { CatalogAPI } from "@/api/CatalogAPI";
import { Store } from "@/core/Store";
import { Catalog, CatalogEvents } from "@/core/Catalog";
import { NetLogicTableConfiguration } from "@/core/NetLogicTableConfiguration";

import { OrderStorage } from "@/storage/OrderStorage";
import { Logger } from "@/tools/Logger";

export class NetlogicCatalog {
  static lastUpdate = null;

  static instance = null;

  static getInstance() {
    if (!NetlogicCatalog.instance) {
      NetlogicCatalog.instance = new NetlogicCatalog();
    }

    return NetlogicCatalog.instance;
  }

  canBeUpdated(timestamp) {
    if (!NetlogicCatalog.lastUpdate) {
      return true;
    }

    if (!timestamp) {
      return true;
    }

    return timestamp != NetlogicCatalog.lastUpdate;
  }

  async getNetlogicCatalog() {
    const netLogicConfigs = NetLogicTableConfiguration.fromStorage();

    const netLogicClient = new NetLogicAPI({
      baseUrl: netLogicConfigs.apiUrl,
      apiToken: netLogicConfigs.apiToken,
    });

    const catalogResponse = await netLogicClient.getCatalog();

    if (catalogResponse.getErrors([]).length > 0) {
      Logger.error(`Falha na consulta do cardápio da NetLogic`, {
        configs: {
          baseUrl: netLogicConfigs.apiUrl,
          apiToken: netLogicConfigs.apiToken,
        },
        response: catalogResponse
      });
    }

    const rawCatalog = catalogResponse.getData([]);

    const skus = {};
    for (const category of rawCatalog) {
      for (const item of category?.menuItens || []) {
        if (item.miMasterObjNum) {
          skus[item.miMasterObjNum] = {
            price: item.price,
            available: item.outOfMenuItem ? false : true,
          };
        }

        for (const key of ["AllowedCondiments", "RequiredCondiments"]) {
          for (const condiment of item[key] || []) {
            const groupItens = condiment?.groupItens || [];

            for (const groupItem of groupItens) {
              if (groupItem.miMasterObjNum) {
                skus[groupItem.miMasterObjNum] = {
                  price: groupItem.price,
                  available: groupItem.outOfMenuItem ? false : true,
                };
              }
            }
          }
        }
      }
    }

    return skus;
  }

  async getStoreCatalog() {
    const catalog = await Store.catalog();

    OrderStorage.updateCartCatalogService(catalog.service);

    Catalog.executeOnSchema(catalog, (i) => {
      i.visible = true;
      i.available = true;
    });

    return catalog;
  }

  async syncronize() {
    Logger.info(`Sincronizando cardápio`);

    const skus = await this.getNetlogicCatalog();
    const keys = Object.keys(skus);

    const storeCatalog = await this.getStoreCatalog();

    Catalog.executeOnSchema(storeCatalog, (item) => {
      if (["catalog", "category", "question", "size"].includes(item.type)) {
        return;
      }

      const component = item.type == "option" ? item : item.sizes[0];
      if (!component?.sku) {
        item.show = false;
        item.available = false;
        item.visible = false;
        return;
      }

      if (component?.sku && !keys.includes(component?.sku)) {
        item.show = false;
        item.available = false;
        item.visible = false;

        return;
      }

      const show = skus[component.sku]?.available === true || false;

      component.price = skus[component.sku]?.price;
      component.show = show;
      component.available = show;
      component.visible = show;
    });

    let availableCatalog = CatalogAPI.removeHiddensAndASized(storeCatalog);
    availableCatalog = Catalog.updateCatalogAvailability(availableCatalog);
    return availableCatalog;
  }

  async update(timestamp) {
    try {
      if (this.canBeUpdated(timestamp)) {
        const catalog = await this.syncronize();

        const storeAlias = Store.alias;
        await Catalog.memorizeCatalog(storeAlias, catalog);

        NetlogicCatalog.lastUpdate = timestamp;

        window.dispatchEvent(new CustomEvent(CatalogEvents.CATALOG_UPDATED));

        Logger.info(`Evento de atualização do cardápio processado`, {
          timestamp: timestamp,
        });
      } else {
        Logger.warn(`Evento de atualização do cardápio ignorado`, {
          newTimestamp: timestamp,
          lastUpdate: NetlogicCatalog.lastUpdate,
        });
      }
    } catch (err) {
      Logger.error(
        `Falha no processamento do evento do atualização do cardápio (${timestamp})`,
        err
      );
    }
  }
}
